import ErrorManager from '@/common/utils/ErrorManager';
import { sortValue } from '@/models/SortType';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState } from '@/plugins/store';
import { ActionTree } from 'vuex';
import SuiviInterventionPreventiveService, {
    FilterInterventionsPreventives
} from '../services/SuiviInterventionPreventiveService';
import { SuiviInterventionActionTypes, SuiviInterventionPreventiveActions } from './action-types';
import { SUIVI_INTERVENTION_PREVENTIVE } from './mutation-types';
import { SuiviInterventionPreventiveState } from './state';
import axios from 'axios';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { SUIVI_INTERVENTION } from '@/modules/SuiviInterventions/store/mutation-types';
import { FilterPreventif } from '@/modules/Preventif/services/PreventifService';

let abortController: AbortController | null = null;

export const actions: ActionTree<SuiviInterventionPreventiveState, RootState> = {
    async [SuiviInterventionPreventiveActions.GET_SUIVIS_INTERVENTION](
        { commit }: SuiviInterventionActionTypes,
        payload: FilterInterventionsPreventives
    ) {
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();

        const { isBim, ...params } = payload;
        const serviceParams = params;
        delete (serviceParams as any).isBim;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_PAGE}`, payload.page, { root: true });
            commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_IS_LOADING}`, true, { root: true });
        } else {
            commit(SUIVI_INTERVENTION.SET_PAGE, payload.page);
            commit(SUIVI_INTERVENTION.SET_ERROR, undefined);
            commit(SUIVI_INTERVENTION.SET_IS_LOADING, true);
        }
        try {
            const response = await SuiviInterventionPreventiveService.getSuiviInterventionsPreventives(
                serviceParams,
                abortController.signal
            );
            if (response.status === 200 && response.data !== undefined) {
                if (payload.page === 0) {
                    if (isBim) {
                        commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_ITEMS}`, response.data, {
                            root: true,
                        });
                        commit(
                            `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_NB_OF_PAGES}`,
                            response.headers.pages,
                            { root: true }
                        );
                        commit(
                            `equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_COUNT}`,
                            response.headers.count,
                            {
                                root: true,
                            }
                        );
                    } else {
                        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_ITEMS, response.data);
                        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_SCROLL, 0);
                        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_NB_OF_PAGES, response.headers.pages);
                        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_COUNT, response.headers.count);
                    }
                } else {
                    if (isBim) {
                        commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_ADD_ITEMS}`, response.data, {
                            root: true,
                        });
                    } else {
                        commit(SUIVI_INTERVENTION_PREVENTIVE.ADD_ITEMS, response.data);
                    }
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_IS_LOADING}`, false, { root: true });
            } else {
                commit(SUIVI_INTERVENTION_PREVENTIVE.SET_IS_LOADING, false);
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_IS_LOADING}`, false, { root: true });
            } else {
                commit(SUIVI_INTERVENTION_PREVENTIVE.SET_IS_LOADING, false);
                commit(SUIVI_INTERVENTION_PREVENTIVE.SET_ERROR, error);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
    async [SuiviInterventionPreventiveActions.NAV_FILTER](
        { commit }: SuiviInterventionActionTypes,
        payload: FilterInterventionsPreventives
    ) {
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_FROM, payload.dateDebutPeriodeRechercheParDate);
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_TO, payload.dateFinPeriodeRechercheParDate);
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_STATUS, payload.etats);
        commit(SUIVI_INTERVENTION_PREVENTIVE.RESET_ITEMS);
        this.dispatch(
            `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.GET_SUIVIS_INTERVENTION}`,
            {
                page: 0,
                etats: payload.etats,
                sort:
                    this.state.suiviInterventionPreventiveModuleState.sort &&
                    sortValue(
                        this.state.suiviInterventionPreventiveModuleState.sort?.sortData,
                        this.state.suiviInterventionPreventiveModuleState.sort?.sortStatus
                    ),
                dateDebutPeriodeRechercheParDate: payload.dateDebutPeriodeRechercheParDate,
                dateFinPeriodeRechercheParDate: payload.dateFinPeriodeRechercheParDate,
                adresseSiteIds: payload.siteIds,
            }
        );
    },
    async [SuiviInterventionPreventiveActions.SET_FILTER](
        { commit }: SuiviInterventionActionTypes,
        payload: FilterInterventionsPreventives
    ) {
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_STATUS, payload);
    },
    async [SuiviInterventionPreventiveActions.SET_DATE](
        { commit }: SuiviInterventionActionTypes,
        payload: FilterInterventionsPreventives
    ) {
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_FROM, payload.dateDebutPeriodeRechercheParDate);
        commit(SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_PREDICTED_DATE_TO, payload.dateFinPeriodeRechercheParDate);
    },

    async [SuiviInterventionPreventiveActions.EXPORT_XLS_INTERVENTION_PREVENTIVE](
        { commit }: SuiviInterventionActionTypes,
        payload: FilterPreventif
    ) {
        const { isBim, ...params } = payload;
        delete (params as any).isBim;
        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_EXPORT_XLS_IS_LOADING}`, true, {
                root: true,
            });
        } else {
            commit(SUIVI_INTERVENTION_PREVENTIVE.SET_EXPORT_XLS_IS_LOADING, true);
        }

        try {
            const response = await SuiviInterventionPreventiveService.getExportXlsInterventions(payload);
            if (response.status === 200 && response.data !== undefined) {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = 'export_interventions_preventives.xlsx';
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_EXPORT_XLS_IS_LOADING}`, false, {
                    root: true,
                });
            } else {
                commit(SUIVI_INTERVENTION_PREVENTIVE.SET_EXPORT_XLS_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_EXPORT_XLS_IS_LOADING}`, false, {
                    root: true,
                });
            } else {
                commit(SUIVI_INTERVENTION_PREVENTIVE.SET_EXPORT_XLS_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },
};
